import { useEffect, useRef } from 'react';
import * as React from 'react';
import Column from '@sdk/components/ui/spacing/Column';
import PlusWithLabel from '@sdk/components/ui/PlusWithLabel';
import Padding from '@sdk/components/ui/spacing/Padding';
import useAuth from '@sdk/authentication/useAuth';
import gettext from '@sdk/lang/gettext';
import { Feature } from '@sdk/common/features';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';

import PremiumGuard from '../../../../premiumGuard/PremiumGuard';
import usePremiumGuard from '../../../../premiumGuard/usePremiumGuard';
import { useSettingsErrorsContext } from '../../../SettingsErrorContext';
import { TabType } from '../../../types';

import ShiftAlarmRow from './ShiftAlarmRow';
import type { UseShiftAlarmsType } from './useShiftAlarms';

type Props = {
  onChange?: (props: number[]) => void;
  shiftAlarmEnabled: boolean;
} & UseShiftAlarmsType;

const ShiftAlarms: React.FC<React.PropsWithChildren<Props>> = ({
  onChange,
  alarmsModel,
  shiftAlarmEnabled,
  addAnotherAlarm,
  setAlarmsModel,
  canAddAnotherAlarm,
  removeAlarm,
  onChangeAlarmUnit,
  onChangeAlarmValue,
}) => {
  const { isOrgFeatureEnabled, currentUser } = useAuth();
  const { getPremiumGuardTooltipContent } = usePremiumGuard();
  const { toggleError } = useSettingsErrorsContext();
  const initialModel = useRef(alarmsModel);

  useEffect(() => {
    const alarms = alarmsModel.map((alarm) => alarm.value);
    onChange(alarms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmsModel]);

  useEffect(() => {
    if (!shiftAlarmEnabled) {
      alarmsModel.forEach((alarmModel) => toggleError(TabType.notifications, alarmModel.id, false));
      setAlarmsModel(initialModel.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftAlarmEnabled]);

  const validateAlarm = (value: string | number): boolean => {
    return Boolean(
      value &&
        validateNumber(value, {
          [ValidateOptions.integer]: true,
          [ValidateOptions.validNumber]: ValidNumberOption.positive,
        })
    );
  };

  return (
    <Column>
      {alarmsModel.map((alarmModel) => (
        <ShiftAlarmRow
          key={alarmModel.id}
          alarmModel={alarmModel}
          validateAlarm={validateAlarm}
          alarmsModelCount={alarmsModel.length}
          removeAlarm={removeAlarm}
          onChangeAlarmValue={onChangeAlarmValue}
          onChangeAlarmUnit={onChangeAlarmUnit}
        />
      ))}
      {canAddAnotherAlarm() &&
        (isOrgFeatureEnabled(Feature.multipleAlarms) || currentUser.isManager) && (
          <PremiumGuard
            premiumFeature={Feature.multipleAlarms}
            hasAccess={isOrgFeatureEnabled(Feature.multipleAlarms)}
            premiumGuardModalName={Feature.multipleAlarms}
            tooltipContent={getPremiumGuardTooltipContent(Feature.multipleAlarms)}>
            <Padding top={12}>
              <PlusWithLabel label={gettext('Add another reminder')} onClick={addAnotherAlarm} />
            </Padding>
          </PremiumGuard>
        )}
    </Column>
  );
};

export default ShiftAlarms;
